import { useEffect, useMemo } from 'react';

import { useBoxRef } from '@fhs/utils';
import {
  ShortCodeState,
  useInRestaurantRedemptionContext,
} from '@fhs-legacy/frontend/src/state/loyalty/in-restaurant-redemption';

import { useLoyaltyIncentives } from '../../queries/loyalty.queries';

export enum PersistedRewardTierStatus {
  /** No persisted reward tier is selected, nothing to validate. */
  Inactive = 'inactive',
  /** Waiting on the latest incentives before the selection can be validated. */
  Validating = 'validating',
  /** The persisted reward tier still exists and is currently redeemable. */
  Valid = 'valid',
  /** The persisted reward tier is missing or no longer redeemable. */
  Invalid = 'invalid',
}

/**
 * Revalidates the persisted `inStoreRewardTierId` against the latest loyalty
 * incentives before the QR/order-selection flow is shown. A stored selection is
 * only honored when the tier still exists in the latest response and is
 * `isRedeemable`. When it is missing or no longer redeemable, the persisted
 * selection is cleared so the user is returned to the redeem selection state.
 */
export function useValidatePersistedRewardTier(): PersistedRewardTierStatus {
  const { data, isSuccess } = useLoyaltyIncentives();
  const { inStoreRewardTierId, resetInRestaurantRedemption, shortCodeState } =
    useInRestaurantRedemptionContext();
  const resetInRestaurantRedemptionRef = useBoxRef(resetInRestaurantRedemption);

  const status = useMemo<PersistedRewardTierStatus>(() => {
    if (!inStoreRewardTierId) {
      return PersistedRewardTierStatus.Inactive;
    }
    // Preserve an already-claimed reward so the confirmation screen is not
    // interrupted when a follow-up incentives refetch marks the tier as no
    // longer redeemable (e.g. after the points were spent).
    if (shortCodeState === ShortCodeState.Claimed) {
      return PersistedRewardTierStatus.Valid;
    }
    // Wait for the latest incentives response before validating.
    if (!isSuccess) {
      return PersistedRewardTierStatus.Validating;
    }

    const selectedRewardTier = data?.rewardTiers?.find(tier => tier.id === inStoreRewardTierId);
    return selectedRewardTier?.isRedeemable
      ? PersistedRewardTierStatus.Valid
      : PersistedRewardTierStatus.Invalid;
  }, [data?.rewardTiers, inStoreRewardTierId, isSuccess, shortCodeState]);

  useEffect(() => {
    if (status === PersistedRewardTierStatus.Invalid) {
      resetInRestaurantRedemptionRef.current?.();
    }
  }, [status, resetInRestaurantRedemptionRef]);

  return status;
}
